import { useState, useEffect } from "react";
import { Badge, DataTable, Link, Page, Card, Filters } from "@shopify/polaris";
import { formatToCurrency, getTableFooter, getRequest } from "../../shared/components/functions";
import { addFilters, applyFilters, filtersToApiUrl, handleFilterChange, limitOptions, removeAllFilters, useUrl } from "../../shared/filters/filterfunctions";
import { useLocation, useNavigate } from "react-router-dom";
import { PageSpinner } from "../../shared/components/spinner/PageSpinner";
import { ImageThumbNailViewer, getPrimaryS3ImageKey } from "../../shared/components/s3_image_display/ImageThumbNailViewer";

export default function ProductsPage() {
  const url = useUrl();
  const location = useLocation();
  const navigate = useNavigate();

  const statusOptions = [
    { label: "All", value: "all" },
    { label: "Active", value: "0" },
    { label: "Distcontinued", value: "1" },
  ];

  const [loaded, setLoaded] = useState(false);
  const [items, setItems] = useState([]);
  const [sortString, setSortString] = useState("");
  const [filters, setFilters] = useState(
    new Map([
      ["limit", { type: "limit", name: "Limit", value: url.get("limit") || "100", options: limitOptions }],
      ["page", { type: "page", name: "Page", value: url.get("page") || "1" }],
      ["q", { type: "text", name: "Q", value: url.get("q") || "" }],
      ["name", { type: "text", name: "Name", value: url.get("name") || "" }],
      ["reference", { type: "text", name: "Reference", value: url.get("reference") || "" }],
      ["supplier", { type: "text", name: "Supplier", value: url.get("supplier") || "" }],
      ["status", { type: "status", name: "Status", value: url.get("status") || "all", options: statusOptions }],
    ])
  );

  useEffect(() => {
    async function fetchData() {
      let filterString = filtersToApiUrl(filters);
      const data = await getRequest(`/api/products?${filterString}${sortString}`);
      setItems(data);
      setLoaded(true);
      if (window.location.pathname === location.pathname) navigate(`${window.location.pathname}?${filterString}`);
    }
    if (filters) fetchData();
  }, [filters, sortString]);

  const handleSort = (columnIndex, direction) => {
    // Map table columns to backend sort keys
    const columnToSortKey = {
      0: "Onderdelen.ProductID", // Code
      2: "Onderdelen.ProductNameNL", // Product name
      3: "Onderdelen.Productref", // Product reference
      4: "klant.NAAM", // Supplier name
      5: "Onderdelen.Discontinued", // Status
      6: "StockMagazijn", // computed alias
      7: "StockTrianval", // computed alias
      8: "OnOrder", // computed alias: open pieces on purchase orders
      9: "Onderdelen.Eenheidsaankoopprijs", // Purchase price
      10: "Onderdelen.[Eenheids verkoopprijs]", // Selling price (spaced name)
    };

    const sortKey = columnToSortKey[columnIndex];
    if (!sortKey) return;
    const sortDir = direction === "descending" ? "desc" : "asc";
    setSortString(`&sort_by=${sortDir}(${sortKey})`);
  };

  const rows = Array.isArray(items)
    ? items.map((item) => {
        const productImageS3Key = getPrimaryS3ImageKey(item.Image);

        return [
          <Link removeUnderline url={"/products/" + item.ProductID}>
            {item.ProductID}
          </Link>,
          <ImageThumbNailViewer s3Key={productImageS3Key} key={`product_img_${item.ProductID}`} />,
          item.ProductNameNL,
          item.Productref,
          <Link removeUnderline url={`/companies/${item.supplier.ID}`}>
            {item.supplier.name}
          </Link>,
          <Badge tone={item.Discontinued ? "critical" : "success"}>{item.Discontinued ? "Discontinued" : "Active"}</Badge>,
          item.StockMagazijn,
          item.StockTrianval,
          item.OnOrder > 0 ? <Badge tone="warning">{String(item.OnOrder)}</Badge> : item.OnOrder,
          formatToCurrency(item.Eenheidsaankoopprijs),
          formatToCurrency(item["Eenheids verkoopprijs"]),
        ];
      })
    : [];

  if (!loaded) return <PageSpinner />;

  return (
    <Page title="Products" fullWidth>
      <Card>
        <Filters
          queryValue={filters.get("q").value}
          filters={addFilters(filters, setFilters)}
          appliedFilters={applyFilters(filters, setFilters)}
          onClearAll={() => removeAllFilters(filters, setFilters)}
          onQueryChange={(e) => handleFilterChange(e, "q", filters.get("q").value, setFilters)}
          onQueryClear={() => handleFilterChange("", "q", filters.get("q").value, setFilters)}
          autoComplete={false}
          queryPlaceholder="Filter products"
        />
        <DataTable
          verticalAlign="middle"
          columnContentTypes={["text", "text", "text", "text", "text", "text", "numeric", "numeric", "numeric", "numeric", "numeric"]}
          headings={[
            <b>Code</b>,
            <b>Img</b>,
            <b>Product name</b>,
            <b>Product reference</b>,
            <b>Supplier</b>,
            <b>Status</b>,
            <b>Stock Magazijn</b>,
            <b>Stock Trianval</b>,
            <b>On order</b>,
            <b>Purchase price</b>,
            <b>Selling price</b>,
          ]}
          sortable={[true, false, true, true, true, true, true, true, true, true, true]}
          defaultSortDirection="descending"
          initialSortColumnIndex={0}
          onSort={handleSort}
          rows={rows}
          footerContent={getTableFooter(rows, filters.get("page"), filters.get("limit"), setFilters, "products")}
        />
      </Card>
    </Page>
  );
}
